import { ReactElement } from 'react';

import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { DefaultLayout, Footer } from '@/components/layouts';
import { InventoryDefinitionContent } from '@/components/sections/inventory';
import { PropsBuilder, getPublicationState, useGetInventoryDefinitionData } from '@/graphql/api';
import {
  useGetGlobalQuery,
  useGetInventoryDefinitionsFilteredBySlugQuery,
  useGetMenuQuery,
  useGetStickyAdsForGlossaryQuery,
} from '@/graphql/generated';
import { NextPageWithLayout } from '@/pages/_app';
import { ALPHABET, DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

interface InventoryDefinitionProps {
  preview: boolean;
  slug: string;
}

const InventoryDefinition: NextPageWithLayout<InventoryDefinitionProps> = ({ slug, preview }) => {
  const { content, title, doodle, articles } = useGetInventoryDefinitionData(slug, preview, 'all');
  const relatedArticles = articles?.data.map((a) => a.attributes);

  const letters = ALPHABET;

  const stickyAds = useGetStickyAdsForGlossaryQuery().data?.stickyAds?.data;

  return (
    <InventoryDefinitionContent
      slug={slug}
      doodle={doodle}
      content={content}
      articles={relatedArticles}
      title={title}
      letters={letters}
      stickyAds={stickyAds}
    />
  );
};

InventoryDefinition.getLayout = (page: ReactElement) => {
  const { metadata, preview, menu } = page.props;

  return (
    <DefaultLayout metadata={metadata} menu={menu} preview={preview}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  // Optimization to speed up build time:
  // Incremental Static Regeneration + section "additionalPaths" in next-sitemap.config.js
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params, locale, preview = false } = context;
  const slug = params?.slug as string;

  const propsBuilder = new PropsBuilder();

  const publicationState = getPublicationState(preview);
  const variables = {
    slug,
    publicationState,
    locale: 'all',
  };

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  const result = await propsBuilder.withQuery(useGetInventoryDefinitionsFilteredBySlugQuery, {
    variables,
    getMetadata: (data) => data.inventoryDefinitions?.data[0]?.attributes?.metadata,
    isPrivate: preview,
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });
  propsBuilder.withQuery(useGetStickyAdsForGlossaryQuery);

  propsBuilder.withQuery(useGetStickyAdsForGlossaryQuery);

  const inventoryDefinition = result.inventoryDefinitions?.data[0];

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON]);

  if (!inventoryDefinition) {
    return {
      notFound: true,
      revalidate: PAGE_REVALIDATE_TIME,
    };
  }

  return {
    props: {
      ...(await propsBuilder.build(lang)),
      preview,
      slug,
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default InventoryDefinition;
